import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { Observable, throwError } from 'rxjs';
import { tap, map, finalize, catchError } from 'rxjs/operators';

import { AppState } from '../../store/state/app.state';
import { CompanyProfile, LegalType, PartnerOptions, ProductsOptions } from '../models/company-profile.model';
import { ResponseModel } from '../../shared/models';
import {
  LoadCompanyProfile,
  UpdateCompanyProfile,
  IncrementLoadingRequestsCount,
  DecrementLoadingRequestsCount,
  LoadLegalTypes,
  UpdateValidations,
  UpdateProfileUpdatedAt,
  LoadOnboarding,
  LoadLegalCountryTranslate
} from '../store/actions/company-profile.actions';
import { ImageModel } from 'common/src/models';
import { environment } from 'projects/workspace/src/environments/environment';
import { DisplayToaster } from '../../shared/decorators/toaster';
import { ToasterService } from 'common/src/modules/ui-components/toaster';
import { OnboardingModel } from '../models/onboarding.model';
import { SandboxModel } from 'common/src/modules/quick-onboarding/sandbox.model';
import { SubscriptionPricesConfigModel } from '../models/subscription-prices-config.model';
import { LoadUserQuickActions, LoadUserShortcuts } from '../../store/actions/shared.actions';
import { AuthService } from 'common/src/auth/auth.service';
import { SubscriptionPlanEnum } from '../../../../../../common/src/modules/subscription-activation/subscription-activation.emum';

@Injectable({
  providedIn: 'root',
})
export class AdministrationsApiService {
  private readonly apiEndpoint: string = `${environment.javaApiVersion}/company`;

  constructor(
    private readonly authService: AuthService,
    private readonly dialog: MatDialog,
    private readonly http: HttpClient,
    private readonly store: Store<AppState>,
    private readonly toasterService: ToasterService,
  ) {}


  @DisplayToaster({showErrorMessage: true})
  public getCompanyProfile(): Observable<CompanyProfile> {
    this.store.dispatch(IncrementLoadingRequestsCount());

    return this.http.get<ResponseModel<CompanyProfile>>(`${this.apiEndpoint}/profile`)
      .pipe(
        tap((response: ResponseModel<CompanyProfile>) => {
          this.store.dispatch(LoadCompanyProfile({ companyProfile: response.data as CompanyProfile }));
          this.store.dispatch(UpdateProfileUpdatedAt({ updatedAt: new Date() }));
        }),
        map((data: ResponseModel<CompanyProfile>) => data.data),
        finalize(() => this.store.dispatch(DecrementLoadingRequestsCount()))
      );
  }

  @DisplayToaster({showErrorMessage: true})
  public finishCompanyProfileSetup(wid: number, exitSandbox = false): Observable<CompanyProfile> {
    this.store.dispatch(IncrementLoadingRequestsCount());
    const params = new HttpParams()
      .set('', wid.toString())
      .set('exitSandbox', exitSandbox.toString());

    return this.http.request<ResponseModel<CompanyProfile>>('patch', `${this.apiEndpoint}/profile/finish-setup`, {params})
      .pipe(
        tap((response: ResponseModel<CompanyProfile>) => {
          this.store.dispatch(LoadCompanyProfile({ companyProfile: response.data as CompanyProfile }));
          this.store.dispatch(UpdateProfileUpdatedAt({ updatedAt: new Date() }));
        }),
        map((data: ResponseModel<CompanyProfile>) => data.data),
        finalize(() => this.store.dispatch(DecrementLoadingRequestsCount()))
      );
  }

  public updateCompanyProfileSbcManagement(
    companyProfile: CompanyProfile,
    ignore?: string,
    activate= false,
  ): Observable<ResponseModel<CompanyProfile>> {
    const params = {};
    params['wid'] = companyProfile.workspaceId;

    if (ignore) {
      params['ignore'] = ignore;
    }
    if (activate) {
      params['activate'] = activate;
    }

    this.store.dispatch(IncrementLoadingRequestsCount());

    return this.http.put<ResponseModel<CompanyProfile>>(`${this.apiEndpoint}/profile/sbc-management`, companyProfile, { params })
      .pipe(
        tap((response: ResponseModel<CompanyProfile>) => {
          this.store.dispatch(UpdateCompanyProfile({ companyProfile: response.data as CompanyProfile }));
          this.store.dispatch(UpdateValidations({ validations: response.validations }));
          this.store.dispatch(UpdateProfileUpdatedAt({updatedAt: new Date()}));
          this.store.dispatch(LoadUserShortcuts({ userId: this.authService.getUser().id }));
          this.store.dispatch(LoadUserQuickActions({ userId: this.authService.getUser().id }));
        }),
        catchError(error => {
          if (error.error.errors.includes('CHANGE_LEGAL_TYPE_OPEN_DOCUMENT_SMALL_BUSINESS_VALIDATION_ERROR')) {
            return throwError(error);
          }
          // else if (error.error.errors.includes('FREE_PLAN_TO_ACTIVE_REPORTING_TYPE_VALIDATION')) {
          //   this.dialog.open(InfoModalComponent, {
          //     data: {
          //       title: 'SUBSCRIPTION_PLAN.DOWNGRADE_PLAN_MODAL_TTL',
          //       message: 'SUBSCRIPTION_PLAN.DOWNGRADE_PLAN_MODAL_MSG_2',
          //       confirmBtnText: 'BUTTON.CLOSE',
          //       hideCancelBtn: true,
          //       titleIcon: 'alert-triangle',
          //       titleColor: 'yellow-400',
          //       confirmBtnIcon: 'close'
          //     }
          //   });
          // }
          else {
            this.toasterService.notify({
              type: ToasterService.MESSAGE_TYPE_ERROR,
              message: error.error.message || error.error.errors || 'COMMON.SOME_ERROR'
            });
          }
          return throwError(error);
        }),
        finalize(() => this.store.dispatch(DecrementLoadingRequestsCount()))
      );
  }

  public updateCompanyProfileInfo(
    companyProfile: CompanyProfile,
    ignore?: string,
    activate= false,
  ): Observable<ResponseModel<CompanyProfile>> {
    const params = {};
    params['wid'] = companyProfile.workspaceId;

    if (ignore) {
      params['ignore'] = ignore;
    }
    if (activate) {
      params['activate'] = activate;
    }

    this.store.dispatch(IncrementLoadingRequestsCount());

    return this.http.put<ResponseModel<CompanyProfile>>(`${this.apiEndpoint}/profile/info`, companyProfile, { params })
      .pipe(
        tap((response: ResponseModel<CompanyProfile>) => {
          this.store.dispatch(UpdateCompanyProfile({ companyProfile: response.data as CompanyProfile }));
          this.store.dispatch(UpdateValidations({ validations: response.validations }));
          this.store.dispatch(UpdateProfileUpdatedAt({updatedAt: new Date()}));
        }),
        catchError(error => {
          if (error.error.errors.includes('CHANGE_LEGAL_TYPE_OPEN_DOCUMENT_SMALL_BUSINESS_VALIDATION_ERROR')) {
            return throwError(error);
          } else {
            this.toasterService.notify({
              type: ToasterService.MESSAGE_TYPE_ERROR,
              message: error.error.message || error.error.errors || 'COMMON.SOME_ERROR'
            });
          }
          return throwError(error);
        }),
        finalize(() => this.store.dispatch(DecrementLoadingRequestsCount()))
      );
  }

  @DisplayToaster({showErrorMessage: true})
  public exitSandbox(sandbox: SandboxModel): Observable<ResponseModel<CompanyProfile>> {
    const params = new HttpParams().set('wid', sandbox.workspaceId.toString());
    return this.http.post<ResponseModel<CompanyProfile>>(`${this.apiEndpoint}/profile/sandbox/exit`, {...sandbox}, {params})
      .pipe(
        tap((response: ResponseModel<CompanyProfile>) => {
          this.store.dispatch(LoadCompanyProfile({ companyProfile: response.data as CompanyProfile }));
        }),
    );
  }

  public getCompanySubscriptionPricesConfig(): Observable<SubscriptionPricesConfigModel> {
    return this.http.get<ResponseModel<SubscriptionPricesConfigModel>>(`${environment.javaApiVersion}/utils/subscription/prices`)
      .pipe(
        map((data: ResponseModel<SubscriptionPricesConfigModel>) => data.data),
      );
  }

  public applyPromoCode(wid: number, promoCode: string): Observable<ResponseModel<CompanyProfile>> {
    return this.http.request<ResponseModel<CompanyProfile>>(
      'post',
      `${environment.javaApiVersion}/workspaces/${wid}/subscription/promo-code/${promoCode}`
    )
      .pipe(
        tap((response: ResponseModel<CompanyProfile>) => {
          this.store.dispatch(LoadCompanyProfile({ companyProfile: response.data as CompanyProfile }));
        }),
      );
  }

  public getLegalCountryTranslate(countryCodeIso3: string): Observable<string> {
    const params = { countryCodeIso3 };
    return this.http.get<ResponseModel<string>>(`${environment.javaApiVersion}/utils/countries/translate`, { params })
      .pipe(
        tap((response: ResponseModel<string>) => {
          this.store.dispatch(LoadLegalCountryTranslate({ legalCountryTranslate: response.data }));
        }),
        map((data: ResponseModel<string>) => data.data)
      );
  }

  @DisplayToaster({showErrorMessage: true})
  public setFullsizeLogo(image: ImageModel): Observable<CompanyProfile> {
    this.store.dispatch(IncrementLoadingRequestsCount());

    return this.http.put<ResponseModel<CompanyProfile>>(`${this.apiEndpoint}/profile/full-size-logo`, image).pipe(
      tap(() => {
        this.store.dispatch(UpdateProfileUpdatedAt({updatedAt: new Date()}));
      }),
      map((data: ResponseModel<CompanyProfile>) => data.data),
      finalize(() => this.store.dispatch(DecrementLoadingRequestsCount()))
    );
  }

  @DisplayToaster({showErrorMessage: true, showSuccessMessage: false, successMsg: 'Image deleted successfully'})
  public deleteFullsizeLogo(image: ImageModel): Observable<CompanyProfile> {
    this.store.dispatch(IncrementLoadingRequestsCount());

    return this.http
      .request<ResponseModel<CompanyProfile>>('delete', `${this.apiEndpoint}/profile/full-size-logo`, { body: image })
      .pipe(
        tap(() => {
          this.store.dispatch(UpdateProfileUpdatedAt({updatedAt: new Date()}));
        }),
        map((data: ResponseModel<CompanyProfile>) => data.data),
        finalize(() => this.store.dispatch(DecrementLoadingRequestsCount()))
      );
  }

  @DisplayToaster({showErrorMessage: true})
  public getLegalTypes(countryCodeIso3: string): Observable<LegalType[]> {
    return this.http
      .get<LegalType[]>(`${environment.javaApiVersion}/utils/profile/legal-types`, {
        params: {
          countryCodeIso3,
        },
      })
      .pipe(
        tap((response: LegalType[]) => {
          this.store.dispatch(LoadLegalTypes({ legalTypes: response }));
        }),
        map((response: LegalType[]) => response)
      );
  }

  public getUploadImageLink(): string {
    return `${environment.javaApiUrl}${environment.javaApiVersion}/image/CP`;
  }

  public isPaymentIntentCompleted(paymentIntent: string): Observable<{ stripeStatus: 'payment_succeeded' }> {
    return this.http.get<ResponseModel<{ stripeStatus: 'payment_succeeded' }>>(`${environment.javaApiVersion}/integration/stripe/${paymentIntent}`)
      .pipe(map((data: ResponseModel<{ stripeStatus: 'payment_succeeded' }>) => data.data));
  }

  @DisplayToaster({showErrorMessage: true})
  public getOnboardingProcess(): Observable<OnboardingModel> {
    return this.http.get<ResponseModel<OnboardingModel>>(`${environment.javaApiVersion}/onboarding`)
      .pipe(
        tap((response: ResponseModel<OnboardingModel>) => {
          this.store.dispatch(LoadOnboarding({ onboarding: response.data }));
          if (response.data.progress.total === response.data.progress.completed) {
            this.getCompanyProfile().subscribe();
          }
        }),
        map((response: ResponseModel<OnboardingModel>) => response.data),
      );
  }

  @DisplayToaster({showErrorMessage: true})
  public completeOnboardingProcess(): Observable<any> {
    return this.http.request<ResponseModel<any>>('post', `${environment.javaApiVersion}/onboarding/complete`);
  }

  @DisplayToaster({showErrorMessage: true})
  public skipPdfDesignerProcess(): Observable<any> {
    return this.http.request<ResponseModel<any>>('post', `${environment.javaApiVersion}/onboarding/pdf-designer-skip`);
  }

  @DisplayToaster({showErrorMessage: true})
  public skipBankAccountProcess(): Observable<any> {
    return this.http.request<ResponseModel<any>>('post', `${environment.javaApiVersion}/onboarding/bank-account-skip`);
  }

  @DisplayToaster({showErrorMessage: true})
  public skipWarehouseProcess(): Observable<any> {
    return this.http.request<ResponseModel<any>>('post', `/warehouse/onboarding/skip`);
  }

  @DisplayToaster({showErrorMessage: true})
  public skipImportProcess(): Observable<any> {
    return this.http.request<ResponseModel<any>>('post', `/import/onboarding/skip`);
  }

  @DisplayToaster({showErrorMessage: true})
  public skipDeliveryServiceProcess(): Observable<any> {
    return this.http.request<ResponseModel<any>>('post', `/delivery-service/onboarding/skip`);
  }

  public checkOrigin(): Observable<any> { // returns "200 OK" if workspace activated
    return this.http.get(`${this.apiEndpoint}/profile/check-origin`);
  }

  public resendConfirmationEmail(): Observable<any> {
    return this.http.request('post', `${this.apiEndpoint}/profile/resend`);
  }

  public updateProductsSettings(wid: number, body: ProductsOptions): Observable<CompanyProfile> {
    const params = new HttpParams().set('wid', wid.toString())
    return this.http.request<ResponseModel<CompanyProfile>>('post', `${this.apiEndpoint}/profile/settings/product`, {params, body})
      .pipe(
        tap((response: ResponseModel<CompanyProfile>) => {
          this.store.dispatch(LoadCompanyProfile({ companyProfile: response.data }));
        }),
        map((data: ResponseModel<CompanyProfile>) => data.data)
      );
  }

  public updatePartnersSettingsSetup(wid: number, body: PartnerOptions): Observable<CompanyProfile> {
    const params = new HttpParams().set('wid', wid.toString())
    return this.http.request<ResponseModel<CompanyProfile>>('post', `${this.apiEndpoint}/profile/settings/partner`, {params, body})
      .pipe(
        tap((response: ResponseModel<CompanyProfile>) => {
          this.store.dispatch(LoadCompanyProfile({ companyProfile: response.data }));
        }),
        map((data: ResponseModel<CompanyProfile>) => data.data)
      );
  }

  @DisplayToaster({showErrorMessage: true})
  public generateShopifyConfirmationUrl(
    wid: number,
    body: {
      plan: SubscriptionPlanEnum,
      extensions: {
        inventoryManagement: boolean,
        onlineSales: boolean,
        timeTracking: boolean
      },
      promoCode: string
    }
  ): Observable<{ confirmationUrl: string }> {
    const params = new HttpParams().set('wid', wid.toString());
    return this.http.post<ResponseModel<{ confirmationUrl: string }>>(`${environment.javaEcommerceApiVersion}/workspaces/${wid}/subscriptions/shopify/generate`, body, { params })
      .pipe(
        map((data: ResponseModel<{ confirmationUrl: string }>) => data.data)
      );
  }

  @DisplayToaster({showErrorMessage: true})
  public getShopifyState(wid: number): Observable<{ shopifyUrl: string }> {
    return this.http.get<ResponseModel<{ shopifyUrl: string }>>(`${environment.javaEcommerceApiVersion}/workspaces/${wid}/subscriptions/shopify`)
      .pipe(
        map((data: ResponseModel<{ shopifyUrl: string }>) => data.data)
      );
  }

  public showMsg(type: string, message: string | string[]): void {
    this.toasterService.notify({type, message});
  }
}
